import { cn, cssVars } from "@sagot/ui";
import { createContext, useContext, type ReactNode } from "react";

interface ChatThemeValue {
  /** True inside <ChatTheme>: components use `--wg-*` tenant vars instead of platform tokens. */
  themed: boolean;
}

const ChatThemeContext = createContext<ChatThemeValue>({ themed: false });

export function useChatTheme(): ChatThemeValue {
  return useContext(ChatThemeContext);
}

export interface ChatThemeProps {
  /** Tenant brand colour, e.g. "#7a1f2b". */
  primary: string;
  /** Auto-contrast text colour on `primary` (see `onPrimaryFor` in @sagot/constants). */
  onPrimary: string;
  /** Corner radius, e.g. "14px". */
  radius?: string;
  /** `font-family` stack (e.g. a Widget Studio font); omitted = the surrounding font. */
  font?: string;
  className?: string;
  children: ReactNode;
}

/** Tenant theming for widget / web app (CLAUDE.md rule 3). The console omits it and gets platform tokens. */
export function ChatTheme({ primary, onPrimary, radius, font, className, children }: ChatThemeProps) {
  return (
    <ChatThemeContext.Provider value={{ themed: true }}>
      <div className={cn("contents", font && "font-[family-name:var(--wg-font)]", className)} style={cssVars({ "--wg-primary": primary, "--wg-on-primary": onPrimary, "--wg-radius": radius, "--wg-font": font })}>
        {children}
      </div>
    </ChatThemeContext.Provider>
  );
}

/** Primary-action classes: tenant fill when themed, platform `.btn-primary` otherwise. */
export function primaryActionClass(themed: boolean): string {
  return themed ? "btn bg-[var(--wg-primary)] text-[var(--wg-on-primary)] hover:opacity-90" : "btn btn-primary";
}

/** Selected day / time chip: tenant fill when themed, platform primary otherwise. */
export function selectedChipClass(themed: boolean): string {
  return themed
    ? "border-[var(--wg-primary)] bg-[var(--wg-primary)] text-[var(--wg-on-primary)]"
    : "border-primary bg-primary text-on-primary";
}
